// src/pages/KarigarManagement/Job/utils/jobFormUtils.js

import { format, addDays } from "date-fns";

/**
 * Extract metal type from purity string
 */
export const getMetalTypeFromPurity = (purity = "") => {
  const lowerPurity = purity.toLowerCase();
  if (lowerPurity.includes("silver") || purity.includes("925") || purity.includes("999")) {
    return "SILVER";
  }
  if (lowerPurity.includes("platinum")) {
    return "PLATINUM";
  }
  return "GOLD";
};

/**
 * Extract product images from product media array
 */
export const extractProductImages = (product) => {
  if (!product?.media?.length) return [];
  
  return product.media
    .filter((m) => m.type === "image")
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((m) => m.url);
};

/**
 * Extract stones from product
 */
export const extractProductStones = (product) => {
  if (!product?.hasStones || !product?.stones?.length) return [];
  
  return product.stones.map((stone) => ({
    type: stone.type || "",
    weight: stone.weight?.toString() || "",
    quantity: stone.count || 1,
    notes: [stone.shape, stone.settingType, stone.color, stone.clarity]
      .filter(Boolean)
      .join(" - "),
  }));
};

/**
 * Extract capable karigars from product
 */
export const extractProductKarigars = (product) => {
  if (!product?.manufacturing?.capableKarigars?.length) return [];
  
  return product.manufacturing.capableKarigars.map((k) => ({
    karigarId: k.karigarId,
    _id: k.karigarId,
    karigarCode: k.karigarCode,
    name: k.karigarName,
    role: k.role,
    capabilities: k.capabilities,
    priority: k.priority,
    isVerified: k.isVerified,
    productTypeStats: k.productTypeStats,
  }));
};

/**
 * Calculate expected completion date from estimated hours
 */
export const calculateCompletionDate = (estimatedHours, startDate = new Date()) => {
  if (!estimatedHours) return null;
  const estimatedDays = Math.ceil(estimatedHours / 8);
  return format(addDays(startDate, Math.max(estimatedDays, 1)), "yyyy-MM-dd");
};

/**
 * Build job payload from form data
 */
export const buildJobPayload = ({
  formData,
  selectedProduct,
  selectedUser,
  selectedOrder,
  selectedOrderItem,
  selectedKarigar,
  stones,
  activeProductImages,
  needsCustomer,
  needsItemDetails,
}) => {
  const payload = {
    title: formData.title,
    description: formData.description,
    jobType: formData.jobType,
    workType: formData.workType,
    priority: formData.priority,
    expectedCompletionDate: new Date(formData.expectedCompletionDate).toISOString(),
    isUrgent: formData.isUrgent,
    requiresPickup: formData.requiresPickup,
    requiresDelivery: formData.requiresDelivery,
    specialInstructions: formData.specialInstructions || undefined,
    orderId: formData.orderId || undefined,
    orderItemIndex: formData.orderItemIndex,
  };

  // Product Reference if selected
  if (selectedProduct) {
    payload.productId = selectedProduct._id;
    payload.productName = selectedProduct.name;
    payload.productSKU = selectedProduct.sku || selectedProduct.productId;
    payload.categoryId = selectedProduct.categories?.[0] || undefined;
    payload.categoryName = selectedProduct.subType || selectedProduct.type || undefined;
  }

  // Customer info
  if (needsCustomer) {
    payload.customerName = formData.customerName;
    payload.customerPhone = formData.customerPhone;
    if (formData.customerId) payload.customerId = formData.customerId;
  }

  // User reference
  if (selectedUser) {
    payload.userId = selectedUser._id || selectedUser.customerId;
    payload.userName = selectedUser.name;
    payload.userPhone = selectedUser.phoneNumber;
  }

  // Order reference
  if (selectedOrder) {
    payload.orderId = selectedOrder.orderId || selectedOrder._id;
    payload.orderNumber = selectedOrder.orderNumber;
    if (selectedOrderItem !== null && formData.orderItemIndex !== undefined) {
      payload.orderItemIndex = formData.orderItemIndex;
    }
    payload.isCustomerOrder = true;
  }

  // Specifications
  if (!needsItemDetails) {
    payload.specifications = {
      metalType: formData.specifications.metalType || undefined,
      productType: formData.specifications.productType || undefined,
      purity: formData.specifications.purity || undefined,
      weight: formData.specifications.weight
        ? parseFloat(formData.specifications.weight)
        : undefined,
      size: formData.specifications.size || undefined,
      hallmarkRequired: formData.specifications.hallmarkRequired,
      designReference: formData.specifications.designReference || undefined,
      stoneSetting: stones.length > 0 ? stones : undefined,
    };
  }

  // Item details (for repair/alteration)
  if (needsItemDetails) {
    payload.itemDetails = {
      description: formData.itemDetails.description || undefined,
      itemType: formData.itemDetails.itemType || undefined,
      metalType: formData.itemDetails.metalType || undefined,
      estimatedWeight: formData.itemDetails.estimatedWeight
        ? parseFloat(formData.itemDetails.estimatedWeight)
        : undefined,
      condition: formData.itemDetails.condition || undefined,
      customerValuation: formData.itemDetails.customerValuation
        ? parseFloat(formData.itemDetails.customerValuation)
        : undefined,
    };
  }

  // Pricing
  if (formData.pricing.estimatedLabor || formData.pricing.laborRate) {
    payload.pricing = {
      estimatedLabor: parseFloat(formData.pricing.estimatedLabor) || 0,
      laborRateType: formData.pricing.laborRateType,
      laborRate: parseFloat(formData.pricing.laborRate) || undefined,
    };
  }

  // Estimated hours
  if (formData.estimatedHours) {
    payload.estimatedHours = parseFloat(formData.estimatedHours);
  }

  // Karigar assignment
  if (selectedKarigar) {
    payload.karigarId = selectedKarigar._id || selectedKarigar.karigarId;
  }

  return cleanPayload(payload);
};

/**
 * Clean undefined/empty values from payload
 */
export const cleanPayload = (obj) => {
  const cleaned = { ...obj };
  Object.keys(cleaned).forEach((key) => {
    if (cleaned[key] === undefined || cleaned[key] === "") {
      delete cleaned[key];
    } else if (
      typeof cleaned[key] === "object" &&
      cleaned[key] !== null &&
      !Array.isArray(cleaned[key])
    ) {
      cleaned[key] = cleanPayload(cleaned[key]);
      if (Object.keys(cleaned[key]).length === 0) delete cleaned[key];
    }
  });
  return cleaned;
};

/**
 * Populate form from product data
 */
export const getProductFormData = (product, currentJobType) => {
  if (!product) return null;

  const purity = product.metalDetails?.purity || product.purity || "";
  const metalType = getMetalTypeFromPurity(purity);

  return {
    // Product reference
    productId: product._id,
    productName: product.name,
    productSKU: product.sku || product.productId,
    categoryId: product.categories?.[0] || "",
    categoryName: product.subType || product.type || "",

    // Auto-generated title
    title: `${currentJobType || "NEW_MAKING"} - ${product.name}`,
    description: `Making ${product.name} (SKU: ${product.sku || product.productId})`,

    // Specifications
    specifications: {
      metalType,
      productType: product.type || "",
      purity: product.metalDetails?.purity || purity || "",
      weight: product.netWeight?.toString() || product.grossWeight?.toString() || "",
      hallmarkRequired: product.hallmark?.isHallmarked ?? true,
      metalColor: product.metalDetails?.color || "",
    },

    // Estimated time
    estimatedHours: product.manufacturing?.estimatedMakingTime?.default?.toString() || "",
    expectedCompletionDate: product.manufacturing?.estimatedMakingTime?.default
      ? calculateCompletionDate(product.manufacturing.estimatedMakingTime.default)
      : "",

    // Difficulty note
    difficultyNote: product.manufacturing?.difficultyLevel
      ? `Difficulty: ${product.manufacturing.difficultyLevel}`
      : "",
  };
};

/**
 * Populate form from existing job data (for edit)
 */
export const getJobFormData = (job) => {
  if (!job) return null;

  return {
    title: job.title || "",
    description: job.description || "",
    jobType: job.jobType || "CUSTOM_ORDER",
    workType: job.workType || "NEW_MAKING",
    priority: job.priority || "NORMAL",
    customerName: job.customerName || "",
    customerPhone: job.customerPhone || "",
    customerId: job.customerId || "",
    
    // Product reference
    productId: job.productId || "",
    productName: job.productName || "",
    productSKU: job.productSKU || "",
    categoryId: job.categoryId || "",
    categoryName: job.categoryName || "",
    
    // Item details
    itemDetails: {
      description: job.itemDetails?.description || "",
      itemType: job.itemDetails?.itemType || "",
      metalType: job.itemDetails?.metalType || "",
      estimatedWeight: job.itemDetails?.estimatedWeight?.toString() || "",
      condition: job.itemDetails?.condition || "",
      customerValuation: job.itemDetails?.customerValuation?.toString() || "",
    },
    
    // Specifications
    specifications: {
      metalType: job.specifications?.metalType || "",
      productType: job.specifications?.productType || "",
      purity: job.specifications?.purity || "",
      weight: job.specifications?.weight?.toString() || "",
      size: job.specifications?.size || "",
      hallmarkRequired: job.specifications?.hallmarkRequired ?? true,
      designReference: job.specifications?.designReference || "",
    },
    
    // Timeline
    expectedCompletionDate: job.expectedCompletionDate
      ? format(new Date(job.expectedCompletionDate), "yyyy-MM-dd")
      : "",
    estimatedHours: job.estimatedHours?.toString() || "",
    
    // Pricing
    pricing: {
      estimatedLabor: job.pricing?.estimatedLabor?.toString() || "",
      laborRateType: job.pricing?.laborRateType || "PER_GRAM",
      laborRate: job.pricing?.laborRate?.toString() || "",
    },
    
    // Flags
    isUrgent: job.isUrgent || false,
    requiresPickup: job.requiresPickup || false,
    requiresDelivery: job.requiresDelivery || false,
    specialInstructions: job.specialInstructions || "",
    
    // Order reference
    orderId: job.orderId || "",
    orderItemIndex: job.orderItemIndex,
  };
};

/**
 * Extract existing images from job (for edit mode)
 */
export const extractJobImages = (job) => {
  return {
    referenceImages: job?.referenceImages || [],
    itemImages: job?.itemDetails?.images || [],
  };
};

/**
 * Extract stones from job specifications (for edit mode)
 */
export const extractJobStones = (job) => {
  return job?.specifications?.stoneSetting || [];
};

/**
 * Validate step completion
 */
export const validateStep = (step, values, { needsCustomer, needsItemDetails }) => {
  switch (step) {
    case 1:
      return !!values.jobType;
    case 2:
      if (!values.title) return false;
      if (needsCustomer && (!values.customerName || !values.customerPhone)) return false;
      if (needsItemDetails && !values.itemDetails?.description) return false;
      return true;
    case 3:
      return true;
    case 4:
      return true;
    case 5:
      return !!values.expectedCompletionDate;
    case 6:
      return true;
    default:
      return true;
  }
};

/**
 * Get fields to validate for each step
 */
export const getStepValidationFields = (step, { needsCustomer, needsItemDetails }) => {
  switch (step) {
    case 1:
      return ["jobType"];
    case 2:
      const fields = ["title"];
      if (needsCustomer) fields.push("customerName", "customerPhone");
      if (needsItemDetails) fields.push("itemDetails.description");
      return fields;
    case 5:
      return ["expectedCompletionDate"];
    default:
      return [];
  }
};
